import React, { createContext, useContext, useEffect, useState } from 'react';

export interface CartLine { id: number; qty: number; }

interface CartContextValue {
  lines: CartLine[];
  totalCount: number;
  isInCart: (id: number) => boolean;
  addToCart: (id: number, qty?: number) => void;
  removeFromCart: (id: number) => void;
  updateQty: (id: number, qty: number) => void;
  clearCart: () => void;
}

const STORAGE_KEY = 'bfh_cart_v1';
const CartContext = createContext<CartContextValue | null>(null);

const readStored = (): CartLine[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((l): l is CartLine => l && typeof l.id === 'number' && typeof l.qty === 'number')
      : [];
  } catch {
    return [];
  }
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lines, setLines] = useState<CartLine[]>(readStored);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(lines)); } catch { /* storage unavailable — feature degrades to session-only */ }
  }, [lines]);

  const isInCart = (id: number): boolean => lines.some(l => l.id === id);

  const addToCart = (id: number, qty = 1): void => {
    setLines(prev => {
      const existing = prev.find(l => l.id === id);
      if (existing) return prev.map(l => l.id === id ? { ...l, qty: l.qty + qty } : l);
      return [...prev, { id, qty }];
    });
  };

  const removeFromCart = (id: number): void => setLines(prev => prev.filter(l => l.id !== id));

  const updateQty = (id: number, qty: number): void => {
    if (qty < 1) { removeFromCart(id); return; }
    setLines(prev => prev.map(l => l.id === id ? { ...l, qty } : l));
  };

  const clearCart = (): void => setLines([]);

  const totalCount = lines.reduce((sum, l) => sum + l.qty, 0);

  return (
    <CartContext.Provider value={{ lines, totalCount, isInCart, addToCart, removeFromCart, updateQty, clearCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextValue => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartProvider');
  return ctx;
};
